import * as React from 'react';
import { t } from 'i18next';

import { BaseComponent } from '../../BaseComponent';
import { ProjectInstance } from '../../../utils/ProjectInstance';
import { SortType } from '../../../stores/PersistentStore';
import { ProjectManagerStore } from '../../../stores/ProjectManagerStore';
import { ProjectManagerActions } from '../../../actions/ProjectManagerActions';
import { ViewSettingsActions } from '../../../actions/ViewSettingsActions';
import { DialogActions } from '../../../actions/DialogActions';
import { SelectBoxActions } from '../../../actions/SelectBoxActions';
import { LongButton } from '../../LongButton';
import { CenteredImage } from '../../CenteredImage';
import { CenteredText } from '../../CenteredText';
import { UIHelper } from '../../../utils/UIHelper';
import { ProjectHelper } from '../../../utils/ProjectHelper';
import { SwipeButtonGroup, SwipeButtonConfig } from '../../core-swipe-button/SwipeButtonGroup';

import utils = egr.wcf.utils;

interface ProjectTileState {
    selectMode: boolean;
    selectedProjects: Array<string>;
}

interface ProjectTilesGroupProps extends React.Props<ProjectTilesGroup> {
    projects: Array<ProjectInstance>;
    sortType: SortType;
}

export class ProjectTilesGroup extends BaseComponent<ProjectTilesGroupProps, ProjectTileState> {
    protected swipeGroup: SwipeButtonGroup;
    protected sortType: SortType;

    public componentWillMount(): void {
        this.addStoreListener(ProjectManagerStore, this.onChange);
        this.onChange();
    }

    public componentDidUpdate(): void {
        if (this.swipeGroup != null && this.sortType !== this.props.sortType) {
            this.sortType = this.props.sortType;
            this.swipeGroup.closeGroupMembers(null);
        }
    }

    public render(): JSX.Element {
        if (this.state.selectMode) {
            return (
                <div className="long-button-group">
                    {this.props.projects.map((project, index) => {
                        const selected: boolean = project.id != null && this.state.selectedProjects.indexOf(project.id) !== -1;
                        return (
                            <LongButton
                                key={index}
                                className={'middle-size select-mode left-image' + (selected ? ' selected' : '')}
                                shortBorder={true}
                                onClick={() => {this.selectProject(project); }}
                            >
                                <div className="button-image">
                                    <CenteredImage className="" src={project.image} />
                                </div>
                                <CenteredText className="" maxHeight="100%" horizontalAlign="left">
                                    <div className="long-button-header">{UIHelper.toDateString(project.date)}</div>
                                    <div className="long-button-text">{project.name ? project.name : t('Unnamed')}</div>
                                </CenteredText>
                            </LongButton>
                        );
                    })}
                </div>
            );
        }
        return (
            <SwipeButtonGroup
                ref={(node: any) => {this.swipeGroup = node; }}
                className="long-button-group"
                buttonHeight={83}
                buttons={this.props.projects.map(this.getButton)}
            />
        );
    }

    protected getButton = (project: ProjectInstance, index: number): SwipeButtonConfig => {
        return {
            onMainClick: () => { this.loadProject(project); },
            mainElement: (
                <div className="long-button-background">
                    <div
                        className={
                            'long-button middle-size left-image short-border' +
                            (ProjectHelper.canSimplyLoadProject(project.attributes) ? '' : ' half-opacity')
                        }
                    >
                        <div className="button-image">
                            <CenteredImage className="" src={project.image} />
                        </div>
                        <CenteredText className="" maxHeight="100%" horizontalAlign="left">
                            <div className="long-button-header">{UIHelper.toDateString(project.date)}</div>
                            <div className="long-button-text">{project.name ? project.name : t('Unnamed')}</div>
                        </CenteredText>
                    </div>
                </div>
            ),
            rightButtons: [
                {
                    callback: () => { this.deleteProject(project); },
                    classNameActive: 'project-delete-swipe',
                    stretchOnClick: true,
                    className: 'project-delete-swipe'
                },
                {
                    callback: () => { this.showMenu(project); },
                    classNameActive: 'project-edit-swipe',
                    className: 'project-edit-swipe'
                }
            ],
            index: project.id
        };
    };

    protected onChange = (): void => {
        this.setState({
            selectMode: ProjectManagerStore.getState().editMode,
            selectedProjects: ProjectManagerStore.getState().selectedProjects
        });
    };

    protected loadProject = (project: ProjectInstance): void => {
        ProjectManagerActions.loadProject(project.id, project.attributes);
    };

    protected deleteProject = (project: ProjectInstance): void => {
        ProjectManagerActions.deleteProject(project.id).then(
            ProjectManagerActions.updateProjectList).then(() => {
                ViewSettingsActions.closeMessage();
                if (this.swipeGroup != null) {
                    this.swipeGroup.closeGroupMembers(null);
                }
            }
        );
    };

    protected selectProject = (project: ProjectInstance): void => {
        ProjectManagerActions.toggleProjectSelection(project.id);
    };

    protected duplicateProject = (project: ProjectInstance): void => {
        if (utils.string.isNotNullOrEmpty(project.name)) {
            DialogActions.duplicateProject(project.id, project.name);
        }
    };

    protected changeName = (project: ProjectInstance): void => {
        DialogActions.setProjectName(project.id, project.name);
    };

    protected showMenu = (project: ProjectInstance): void => {
        SelectBoxActions.showSelectBox({
            options: [
                {name: t('Clone'), value: 'clone', callback: () => { this.duplicateProject(project); }, className: 'with-icon icon-clone'},
                {name: t('Rename'), value: 'rename', callback: () => { this.changeName(project); }, className: 'with-icon icon-text'}
            ],
            title: t('Project'),
            sender: 'project-manager',
            value: null,
            asOverlay: true
        });
        if (this.swipeGroup != null) {
            this.swipeGroup.closeGroupMembers(null);
        }
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/sidebar/projectmanager/ProjectTilesGroup.tsx